import { t } from 'react-switch-lang';
import moment from 'moment';
import { formatCurrency } from './Helpers';

/**
 * Combines multiple validator functions into a single validator function that
 * runs each validator until a failed validation is found
 * @param {function[]} validators
 * array of validators that takes in the value and returns an error message or undefined
 * @param {boolean} [isRequired] indicates if this a required or optional field; defaults to true
 * @param {string} [customMsg] optional custom message to return if validation fails
 * @returns {(value:string)=>string|undefined}
 * a function that takes in a value to validate and returns the result of validation;
 * the result of validation is either:
 * - an error message string if validation fails
 * - or undefined if validation passes
 */
function combineValidators(validators, isRequired = true, customMsg = null) {
  return (value) => {
    if (!value) return isRequired ? t('Validation_Required') : undefined;

    let result;
    // run validators until we find one that returns a truthy value
    validators.some((validator) => {
      result = validator(value); // result set to error message if validate fails, else undefined
      return result; // returning any truthy value will stop validation
    });
    return result ? ((customMsg && t(customMsg)) || result) : undefined;
  };
}

function getAgeOfMajority(province) {
  switch (province) {
    case 'AB':
    case 'MB':
    case 'ON':
    case 'PE':
    case 'QC':
    case 'SK':
      return 18;
    default:
      return 19;
  }
}
function calculateAge(value) { // birthday is a date
  const birthday = new Date(value);
  if (!(birthday instanceof Date)) {
    return 0;
  }
  const ageDifMs = Date.now() - birthday.getTime();
  const ageDate = new Date(ageDifMs);
  return ageDate.getUTCFullYear() - 1970;
}

/* ******************************* Validator Generator Functions ******************************* */
const ageOfMajority = (province) => (value) => {
  if (!province) return undefined;
  if (calculateAge(value) < getAgeOfMajority(province)) return t('Validation_Invalid_AgeOfMajority');
  return undefined;
};
const validDate = () => (value) => {
  if (!moment(value).isValid()) return t('Validation_Invalid_DateOfBirth');
  if (moment(value).isBefore(moment('1899-12-31'))) return t('Validation_Invalid_DateOfBirth');
  return undefined;
};

const maxLength = (length) => (value) => (value.length > length ? t('Validation_Length_Max').replace('xMax', length) : undefined);
const minLength = (length) => (value) => (value.length < length ? t('Validation_Length_Min').replace('xMin', length) : undefined);
const exactLength = (length) => (value) => (value.length !== length ? t('Validation_Length_Exact').replace('xLength', length) : undefined);
const maxAmount = (max) => (value) => (value > max ? t('Validation_Value_Max').replace('xMax', formatCurrency(max)) : undefined);
const minAmount = (min) => (value) => (value < min ? t('Validation_Value_Min').replace('xMin', formatCurrency(min)) : undefined);
/**
 * Generates a regex validator
 * @param {RegExp} regex regular expression to test against
 * @param {string} failMsg lang key for the text to return if the validation fails
 * @returns {(value:string)=>string|undefined} a validator function
 */
const regexValidator = (regex, failMsg) => (value) => (!regex.test(value) ? t(failMsg) : undefined);

/* **************************************** Validators **************************************** */
const passwordRegex = (value) => {
  const numberRegex = /.*[0-9].*/;
  const upperCaseRegex = /.*[A-Z].*/;
  const specialCharRegex = /.*[ !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~].*/;

  if (!specialCharRegex.test(value)) {
    return t('Registration_Validation_Password_SpecialChar');
  }
  if (!upperCaseRegex.test(value)) {
    return t('Registration_Validation_Password_UpperChar');
  }
  if (!numberRegex.test(value)) {
    return t('Registration_Validation_Password_Number');
  }
  return undefined;
};

const streetContainsPOBoxRegex = /PO[\W]|(?:\b(po box|box|general delivery|gd)\b|\bmain po\b|\brpo\b|\bboite\b|\bgd stn\b|^rr|^(rte|route)|\bcare po\b|^gd|\bgen del\b|\bgendel\b|\bgeneral delivery\b|\bgen delivery\b|\bivr\b|\bmail ?box\b|\bpost office\b|\bpost office delivery\b|\bposte restante\b|^bo|^cluster|^general\b|^pr|^p box|^pb|^p b|\bpost office box\b|\bpostal box\b|\bpostal office box\b|\bboite postale\b|\bboîte postale\b|\bcase postale\b|^cp|^c\. p\.|^c\.p\.|^bp|^b\. p\.|^b p|^boix|^bokx|\bbureau de poste\b|^bx|^c\.p\.box|^p\.|^p|\bpostal code\b)/i;
const emailRegex = regexValidator(/^[\w!#$%&*+/=?`{|}~^-]+(?:\.[\w!#$%&*+/=?`{|}~^-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,32}$/, 'Validation_Invalid_Email');
const numberRegex = regexValidator(/^[0-9]+$/, 'Validation_Type_Number');
const validCharRegex = regexValidator(/^[a-zA-Z0-9&\-.,‘’'âàéêèëîìïôòûùüçÂÀÉÊÈËÎÌÏÔÒÙÜÛÇ()]+(?: [a-zA-Z0-9&\-.,‘’'âàéêèëîìïôòûùüçÂÀÉÊÈËÎÌÏÔÒÙÜÛÇ()]+)*$/, 'Validation_ValidChar_General');
const validNameRegex = regexValidator(/^[a-zA-Z‘’'\-\s]+$/, 'Validation_ValidChar_Maple_Names');
const postalCodeRegex = regexValidator(/^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z] [0-9][ABCEGHJ-NPRSTV-Z][0-9]$/, 'Validation_Invalid_PostalCode');
const phoneNumberRegex = regexValidator(/^(204|226|236|249|250|257|263|289|306|343|354|365|367|368|382|403|416|418|428|431|437|438|450|468|474|506|514|519|548|579|581|584|587|600|604|613|622|639|647|672|683|705|709|742|753|778|780|782|807|819|825|867|873|879|902|905|942)-([2-9]\d{2})-(\d{4})$/, 'Validation_Invalid_PhoneNumber');
const dateOfBirthRegex = regexValidator(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/, 'Validation_Invalid_DateOfBirth');

export const validateCheckbox = (value) => (value ? undefined : true);
export const validateRequired = (value) => (value ? undefined : t('Validation_Required'));

export const validateEmail = combineValidators([maxLength(50), emailRegex]);
export const validatePassword = combineValidators([minLength(10), maxLength(20), passwordRegex]);
export const validateVerificationCode = combineValidators([numberRegex, exactLength(6)], true, 'Validation_Invalid_VerificationCode');

export const validateProxyNumber = combineValidators([exactLength(12), numberRegex]);

export const validateFirstName = combineValidators([maxLength(15), validNameRegex]);
export const validateLastName = combineValidators([maxLength(26), validNameRegex]);
export const validateStreet = combineValidators([
  maxLength(60),
  validCharRegex,
  (value) => (streetContainsPOBoxRegex.test(value) ? t('Validation_Invalid_StreetPOBox') : undefined),
]);
export const validatePOBox = combineValidators([maxLength(23), numberRegex], false);
export const validateCity = combineValidators([maxLength(16), validCharRegex]);
export const validateProvince = combineValidators([(value) => {
  switch (value) {
    case 'AB': case 'BC': case 'MB': case 'NB': case 'NL': case 'NT':
    case 'NS': case 'NU': case 'ON': case 'PE': case 'QC': case 'SK':
    case 'YT':
      return undefined;
    default:
      return true;
  }
}]);
export const validatePostalCode = combineValidators([maxLength(7), postalCodeRegex]);
export const validatePhoneNumber = combineValidators([maxLength(12), phoneNumberRegex]);
export const validateDateOfBirth = (value, province = '') => (
  combineValidators([maxLength(10), dateOfBirthRegex, validDate(), ageOfMajority(province)])(value)
);
export const validateAmount = (value, max, min) => (
  combineValidators([maxAmount(max), minAmount(min)])(value)
);
